/**
 * Audio dB (decibel) scaling utilities for consistent audio level visualization.
 *
 * These constants and functions are shared between:
 * - Peak file generation (server-side waveform storage)
 * - Client-side waveform rendering from audio data
 * - VU meter visualization
 *
 * Using a shared module ensures visual consistency between waveforms and meters.
 */

/**
 * Minimum dB level for audio visualization.
 * Amplitude values below this threshold are treated as silence.
 */
export const AUDIO_MIN_DB = -60;

/**
 * Maximum dB level for audio visualization.
 * Amplitude of 1.0 (full scale) corresponds to 0 dB.
 */
export const AUDIO_MAX_DB = 0;

/**
 * VU meter high threshold in dB. Audio levels above this are considered "high".
 */
export const AUDIO_HIGH_DB = -5;

/**
 * The total dB range used for visualization.
 */
export const AUDIO_DB_RANGE = AUDIO_MAX_DB - AUDIO_MIN_DB;

/**
 * Converts a linear amplitude value (0-1) to decibels.
 *
 * PCM amplitude values are root power quantities, so we use the formula:
 * dB = 20 * log10(amplitude)
 *
 * See: https://en.wikipedia.org/wiki/Decibel
 *
 * @param amplitude Linear amplitude value (0 to 1, where 1 is full scale)
 * @returns Decibel value (0 dB at full scale, negative values for quieter signals)
 */
export function amplitudeToDb(amplitude: number): number {
  if (amplitude === 0) {
    return -Infinity;
  }
  return 20 * Math.log10(amplitude);
}

/**
 * Converts a linear amplitude value to a normalized dB-scaled value (0-1).
 *
 * The amplitude is converted to dB, then mapped from [AUDIO_MIN_DB, AUDIO_MAX_DB]
 * to [0, 1]. Values below AUDIO_MIN_DB map to 0, values above AUDIO_MAX_DB map to 1.
 *
 * @param amplitude Linear amplitude value (0 to 1+)
 * @returns Normalized value between 0 and 1
 */
export function amplitudeToNormalizedDb(amplitude: number): number {
  if (amplitude === 0) {
    return 0;
  }

  const db = 20 * Math.log10(Math.abs(amplitude));
  const clampedDb = Math.max(AUDIO_MIN_DB, Math.min(AUDIO_MAX_DB, db));
  return (clampedDb - AUDIO_MIN_DB) / AUDIO_DB_RANGE;
}

/**
 * Converts a normalized dB value (0-1) back to linear amplitude.
 *
 * This is the inverse of amplitudeToNormalizedDb, useful for generating
 * test data with known peak values.
 *
 * @param normalized Normalized dB value between 0 and 1
 * @returns Linear amplitude value
 */
export function normalizedDbToAmplitude(normalized: number): number {
  if (normalized === 0) {
    // Return the amplitude at the minimum dB threshold
    return Math.pow(10, AUDIO_MIN_DB / 20);
  }
  const db = normalized * AUDIO_DB_RANGE + AUDIO_MIN_DB;
  return Math.pow(10, db / 20);
}
